/**
 * Enhanced Custom Hook: useForecastCopyDuplicate
 *
 * Provides copy functionality with two modes:
 * 1. Simple mode: Relies on useRecalculateMonthlyData hook
 * 2. Enhanced mode: Immediate recalculation after copy
 *
 * Usage (Simple):
 * ```tsx
 * const { handleCopyToNext } = useForecastCopyDuplicate({
 *   config: FTD_CONFIG,
 *   editableStates: { editableNRPU, ... },
 *   monthlyData: monthlyData,
 *   setMonthlyData: setMonthlyData,
 * });
 * ```
 *
 * Usage (Enhanced with immediate recalc):
 * ```tsx
 * const { handleCopyToNext } = useForecastCopyDuplicate({
 *   config: FTD_CONFIG,
 *   editableStates: { editableNRPU, ... },
 *   monthlyData: monthlyData,
 *   setMonthlyData: setMonthlyData,
 *   // Enhanced mode options:
 *   enableImmediateRecalc: true,
 *   monthNames: monthNames,
 *   calculateValues: calculateValues,
 *   lastMonthActiveUsersDuplicate: lastMonthActiveUsersDuplicate,
 * });
 * ```
 */

import { useCallback } from "react";
import {
  CopyType,
  ComponentConfig,
} from "../components/AssetManagement/Overview/PerformanceForecaster/CreateNewForeCast/helpers/Forecastcopyhelper";
import {
  handleCopyToNextDuplicate,
  handleCopyToNextDuplicateWithRecalc,
} from "../components/AssetManagement/Overview/PerformanceForecaster/DuplicateForeCast/helpers/ForecastDuplicateCopyHelper";

interface UseForecastCopyDuplicateParamsBase {
  /**
   * Component configuration defining editable fields and their mappings
   */
  config: ComponentConfig;

  /**
   * Object containing all editable state values
   */
  editableStates: Record<string, string | null>;

  /**
   * Current monthly data from component state
   */
  monthlyData: Record<string, any>;

  /**
   * Setter function for monthly data
   */
  setMonthlyData: (data: Record<string, any>) => void;
}

interface UseForecastCopyDuplicateParamsEnhanced extends UseForecastCopyDuplicateParamsBase {
  /**
   * Enable immediate recalculation after copy
   * If false or undefined, relies on useRecalculateMonthlyData hook
   */
  enableImmediateRecalc: true;

  /**
   * Array of month names (required for enhanced mode)
   */
  monthNames: string[];

  /**
   * Function to calculate derived values (required for enhanced mode)
   */
  calculateValues: (
    month: string,
    newValues: any,
    forcePreviousMonthActiveUsers?: number,
  ) => any;

  /**
   * Last month's active users from previous year (required for enhanced mode)
   */
  lastMonthActiveUsersDuplicate: number;
}

type UseForecastCopyDuplicateParams =
  | UseForecastCopyDuplicateParamsBase
  | UseForecastCopyDuplicateParamsEnhanced;

interface UseForecastCopyDuplicateReturn {
  /**
   * Handler function to copy the current metric to target months
   */
  handleCopyToNext: (copyType: CopyType) => void;
}

/**
 * Type guard to check if enhanced mode is enabled
 */
function isEnhancedMode(
  params: UseForecastCopyDuplicateParams,
): params is UseForecastCopyDuplicateParamsEnhanced {
  return (
    "enableImmediateRecalc" in params && params.enableImmediateRecalc === true
  );
}

/**
 * Custom hook for forecast copy functionality in DuplicateForecastFTD component
 */
export const useForecastCopyDuplicate = (
  params: UseForecastCopyDuplicateParams,
): UseForecastCopyDuplicateReturn => {
  const handleCopyToNext = useCallback(
    (copyType: CopyType) => {
      if (isEnhancedMode(params)) {
        // Enhanced mode: Immediate recalculation
        handleCopyToNextDuplicateWithRecalc(
          copyType,
          params.editableStates,
          params.monthlyData,
          params.config,
          params.monthNames,
          params.calculateValues,
          params.lastMonthActiveUsersDuplicate,
          params.setMonthlyData,
        );
      } else {
        // Simple mode: Let useRecalculateMonthlyData handle it
        handleCopyToNextDuplicate(
          copyType,
          params.editableStates,
          params.monthlyData,
          params.config,
          params.setMonthlyData,
        );
      }
    },
    [params],
  );

  return { handleCopyToNext };
};

/**
 * Type exports for external use
 */
export type {
  UseForecastCopyDuplicateParams,
  UseForecastCopyDuplicateParamsBase,
  UseForecastCopyDuplicateParamsEnhanced,
  UseForecastCopyDuplicateReturn,
};
